import { supabase } from './supabase';
import type { BillingHistory, BillingStatus } from '../types';

export interface UsageStats {
  tier_name: string;
  tier_display_name: string;
  period_start: string;
  period_end: string;
  summaries_used: number;
  summaries_limit: number | null;
  summaries_bonus: number;
  // chat_messages fields retained for back-compat with RPC response; not displayed in UI
  chat_messages_used: number;
  chat_messages_limit: number | null;
}

export interface SummaryLimitCheck {
  allowed: boolean;
  reason?: string;
  usage?: {
    current: number;
    limit: number;
    bonus: number;
  };
}

// Re-export billing types for convenience (some UI imports from billingService)
export type { BillingHistory, BillingStatus };

export class BillingService {
  private static instance: BillingService;

  static getInstance(): BillingService {
    if (!BillingService.instance) {
      BillingService.instance = new BillingService();
    }
    return BillingService.instance;
  }

  /**
   * Check if user can create a summary
   */
  async canCreateSummary(userId: string): Promise<SummaryLimitCheck> {
    try {
      const { data, error } = await supabase.rpc('check_summary_limit', {
        user_uuid: userId
      });

      if (error) {
        console.error('Error checking summary limit:', error);
        // Fail open - allow creation if check fails
        return { allowed: true };
      }

      if (!data || data.length === 0) {
        return { allowed: true };
      }

      const result = data[0];
      const allowed = result.allowed as boolean;
      const current = result.current_count as number;
      const limit = result.limit_count as number | null;
      const bonus = result.bonus_summaries as number;

      if (!allowed && limit !== null) {
        return {
          allowed: false,
          reason: 'Summary limit reached',
          usage: {
            current,
            limit,
            bonus
          }
        };
      }

      return {
        allowed: true,
        usage: {
          current,
          limit: limit || 0,
          bonus
        }
      };
    } catch (error) {
      console.error('Exception checking summary limit:', error);
      // Fail open
      return { allowed: true };
    }
  }

  /**
   * Increment summary count after successful creation
   */
  async incrementSummaryCount(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('increment_summary_usage', {
        user_uuid: userId
      });

      if (error) {
        console.error('Error incrementing summary count:', error);
        // Don't throw - usage tracking failure shouldn't block summary creation
      }
    } catch (error) {
      console.error('Exception incrementing summary count:', error);
    }
  }

  /**
   * Get comprehensive usage stats for UI display
   */
  async getUserUsageStats(userId: string): Promise<UsageStats | null> {
    try {
      const { data, error } = await supabase.rpc('get_user_usage_stats', {
        user_uuid: userId
      });

      if (error) {
        console.error('Error getting user usage stats:', error);
        return null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      const result = data[0];
      return {
        tier_name: result.tier_name as string,
        tier_display_name: result.tier_display_name as string,
        period_start: result.period_start as string,
        period_end: result.period_end as string,
        summaries_used: result.summaries_used as number,
        summaries_limit: result.summaries_limit as number | null,
        summaries_bonus: result.summaries_bonus as number,
        chat_messages_used: result.chat_messages_used as number,
        chat_messages_limit: result.chat_messages_limit as number | null
      };
    } catch (error) {
      console.error('Exception getting user usage stats:', error);
      return null;
    }
  }

  /**
   * Process referral when new user signs up
   */
  async processReferral(referrerUserId: string, newUserId: string): Promise<void> {
    try {
      // Prevent self-referrals
      if (referrerUserId === newUserId) {
        console.warn('Self-referral prevented');
        return;
      }

      // Check if user was already referred
      const { data: existingReferral } = await supabase
        .from('user_referrals')
        .select('id')
        .eq('referred_user_id', newUserId)
        .single();

      if (existingReferral) {
        console.warn('User already has a referral');
        return;
      }

      // Create referral record
      const { error: insertError } = await supabase
        .from('user_referrals')
        .insert({
          referrer_user_id: referrerUserId,
          referred_user_id: newUserId,
          bonus_summaries_granted: 5,
          status: 'active'
        });

      if (insertError) {
        console.error('Error creating referral record:', insertError);
        return;
      }

      // Update referrer's bonus summaries in current usage period
      // This is handled by the get_or_create_current_usage_period function
      // which recalculates bonus summaries from active referrals
      // We just need to trigger a recalculation by calling get_user_usage_stats
      await this.getUserUsageStats(referrerUserId);
    } catch (error) {
      console.error('Exception processing referral:', error);
    }
  }

  /**
   * Get usage stats for current user
   */
  async getCurrentUserUsageStats(): Promise<UsageStats | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }

      return await this.getUserUsageStats(user.id);
    } catch (error) {
      console.error('Exception getting current user usage stats:', error);
      return null;
    }
  }

  /**
   * Back-compat helper: get current month comment summary usage.
   * NOTE: This uses the subscription-based usage RPC and maps to summaries_used.
   */
  async getCurrentMonthUsage(userId: string): Promise<number> {
    if (!userId) return 0;
    try {
      const stats = await this.getUserUsageStats(userId);
      return stats?.summaries_used || 0;
    } catch (error) {
      console.error('Exception getting current month usage:', error);
      return 0;
    }
  }

  /**
   * Get billing status for current user
   */
  async getBillingStatus(): Promise<import('../types').BillingStatus | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }

      // Get billing_enabled flag (tier is derived from subscription model via get_user_usage_stats)
      const { data: userTier, error: tierError } = await supabase
        .from('user_tiers')
        .select('billing_enabled')
        .eq('user_id', user.id)
        .single();

      if (tierError) {
        console.error('Error getting user tier:', tierError);
        return null;
      }

      // Get subscription-based usage/tier (free/pro/enterprise)
      const usageStats = await this.getUserUsageStats(user.id);
      const currentMonthUsage = usageStats?.summaries_used || 0;
      const subscriptionTier = usageStats?.tier_name || 'free';

      // Calculate projected cost (simplified - 50 free, then $0.50 per summary)
      const freeLimit = 50;
      const costPerSummary = 0.5;
      const projectedCost = currentMonthUsage > freeLimit 
        ? (currentMonthUsage - freeLimit) * costPerSummary 
        : 0;

      return {
        billing_enabled: userTier?.billing_enabled || false,
        current_month_usage: currentMonthUsage,
        billing_tier: subscriptionTier,
        projected_cost: projectedCost
      };
    } catch (error) {
      console.error('Exception getting billing status:', error);
      return null;
    }
  }

  /**
   * Get display name for a tier
   */
  getTierDisplayName(tier: string): string {
    const tierNames: Record<string, string> = {
      'free': 'Free',
      'pro': 'Pro',
      'enterprise': 'Enterprise',
      'anonymous': 'Anonymous',
      // Legacy/back-compat
      'paid': 'Pro'
    };
    return tierNames[tier] || tier;
  }

  /**
   * Get billing history records for current user
   */
  async getBillingHistory(): Promise<BillingHistory[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }

      const { data, error } = await supabase
        .from('user_billing_history')
        .select('*')
        .eq('user_id', user.id)
        .order('month_year', { ascending: false });

      if (error) {
        console.error('Error getting billing history:', error);
        return [];
      }

      return (data || []) as BillingHistory[];
    } catch (error) {
      console.error('Exception getting billing history:', error);
      return [];
    }
  }
}

export const billingService = BillingService.getInstance();

